// One mic stream + AudioContext, open only during SPEAK: level meter, MediaRecorder, VAD.
// iOS: any live capture forces PlayAndRecord (earpiece, other audio ducked), so
// the mic must be fully released before anything plays again.
// Silero VAD takes over once loaded; RMS VAD below is the fallback.

import * as vadSilero from './vadSilero'

export const BAR_COUNT = 26

// ~21 ms at 48 kHz: a stable window for RMS.
const FFT_SIZE = 1024

// Speech lives under ~5 kHz, the low ~1/5 of 0-24 kHz.
const METER_BINS = 104

// Hysteresis between the two. Floors are low because gain is not normalised
// (laptop mics give ~0.01-0.03 RMS); noiseFloor raises them in noisy rooms.
const VAD_SPEECH_RMS = 0.008
const VAD_SILENCE_RMS = 0.005
const VAD_NOISE_SPEECH_FACTOR = 2.5
const VAD_NOISE_SILENCE_FACTOR = 1.5

// Sustained speech needed before silence can end the attempt (rejects coughs, fan noise).
const VAD_ARM_MS = 350

// Mid-speech dips shorter than this still count while arming (iPhone gaps
// measured 133-786 ms, one 1220 ms). Oversizing is cheap: the hard cap still applies.
const VAD_ARM_GRACE_MS = 2000

const NOISE_CALIBRATION_MS = 250

// iOS built-in mic arrives quiet (rms ~0.008) and WebKit AGC is unreliable
// under voice processing. Boosted in-graph so meter, RMS and Silero all see it.
const IOS_GAIN_BOOST = 2.5

let stream = null
let audioContext = null
let analyser = null
let recorder = null
let recordedChunks = []
let recorderMimeType = ''
let rafId = null
let levelSubscribers = []
let silenceCallback = null
let silenceMs = 1200
let openedAt = 0
let speechArmedFor = 0
let armGapMs = 0
let trueGapMs = 0
let speechDetectedAt = 0
let sawSpeechEnergy = false
// ponytail: diagnostic only, see peakForTuning().
let peakRms = 0
let lastSpeechThreshold = 0
let quietSince = 0
let noiseFloor = 0
let lastTickAt = 0
let silenceFired = false
let calibrationLogged = false
let stopPromise = null

// BUGFIX-004: track muted by an iOS interruption mid-capture. Its zeros look
// like silence, so both detectors must ignore them.
let trackMuted = false
let capturedTrack = null

// Resolves when the post-release iOS route nudge is done. Await before
// playing right after the mic closes: once iOS routes a stream to the
// earpiece it does not re-route it.
let routeReadyPromise = Promise.resolve()

export function whenRouteReady () {
  return routeReadyPromise
}

// iPadOS 13+ reports a desktop UA; touch points separate it from a Mac.
export function isIOS () {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  if (window.MSStream) return false
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
}

// Explicit routing lever (Safari 16.4+): 'playback' = loudspeaker. A <video>
// re-evaluates the session itself, `new Audio()` does not.
export function setAudioSessionType (type) {
  if (typeof navigator === 'undefined' || !navigator.audioSession) return
  try {
    navigator.audioSession.type = type
  } catch (e) { /* older WebKit that rejects this value */ }
}

// Once true, Silero owns both VAD decisions and RMS owns neither.
let sileroActive = false

// Lets a getUserMedia that resolves after stop() know it lost the race.
let generation = 0

export function isSupported () {
  return !!(
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia &&
    typeof window !== 'undefined' &&
    (window.AudioContext || window.webkitAudioContext)
  )
}

export function isActive () {
  return !!stream
}

// Warm the Silero model before the first attempt; load (and failure) is cached.
export function preloadVad () {
  return vadSilero.load()
}

export function hasDetectedSpeech () {
  return speechDetectedAt > 0
}

// Weaker than hasDetectedSpeech(): is the clip worth uploading? On iPhone Silero
// never loads (wasm OOM) and RMS rarely sustains 350 ms.
export function hasSpeechEnergy () {
  return sawSpeechEnergy
}

// ponytail: diagnostic only, STORY-003 VAD tuning (peak RMS / bar it had to clear).
export function peakForTuning () {
  return peakRms.toFixed(4) + '/' + lastSpeechThreshold.toFixed(4)
}

// Safari (the iOS case) only records MP4/AAC. '' lets MediaRecorder pick.
export function pickMimeType () {
  if (typeof window === 'undefined' || !window.MediaRecorder) return ''
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus'
  ]
  if (typeof window.MediaRecorder.isTypeSupported !== 'function') return ''
  for (let i = 0; i < candidates.length; i++) {
    if (window.MediaRecorder.isTypeSupported(candidates[i])) return candidates[i]
  }
  return ''
}

function onTrackMute () {
  trackMuted = true
}

function onTrackUnmute () {
  trackMuted = false
  // The interruption gap must not count as elapsed quiet time.
  lastTickAt = Date.now()
}

// iOS can return a live but muted track right after backgrounding. Wait for
// `unmute`, capped at 1.5s so a missing event can't strand the user.
function waitForUnmute (track) {
  if (!track || !track.muted) return Promise.resolve()
  return new Promise(function (resolve) {
    const done = function () {
      track.removeEventListener('unmute', done)
      resolve()
    }
    track.addEventListener('unmute', done)
    setTimeout(done, 1500)
  })
}

// onSilence fires once, after a sustained utterance plus silenceMs of quiet.
// Rejects if permission is refused or capture fails.
export async function start (options) {
  const opts = options || {}
  if (!isSupported()) throw new Error('Microphone capture is not supported')

  await stop()

  const myGeneration = ++generation

  // EC/NS off: nothing to cancel with the video paused, and a lighter
  // voice-processing chain lets iOS release PlayAndRecord. AGC on: without it
  // clips came in at rms ~0.008, an SNR problem nothing downstream fixes.
  // The last release pinned the session to 'playback', which can't record.
  setAudioSessionType('auto')

  let captured
  try {
    captured = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: true
      }
    })
  } catch (e) {
    // Some browsers reject unknown constraints outright.
    captured = await navigator.mediaDevices.getUserMedia({ audio: true })
  }

  if (myGeneration !== generation) {
    captured.getTracks().forEach(function (track) { track.stop() })
    return
  }

  const track = captured.getAudioTracks && captured.getAudioTracks()[0]
  await waitForUnmute(track)
  if (myGeneration !== generation) {
    captured.getTracks().forEach(function (track) { track.stop() })
    return
  }

  stream = captured
  silenceCallback = typeof opts.onSilence === 'function' ? opts.onSilence : null
  silenceMs = typeof opts.silenceMs === 'number' ? opts.silenceMs : 1200

  capturedTrack = track || null
  trackMuted = !!(track && track.muted)
  if (track && typeof track.addEventListener === 'function') {
    track.addEventListener('mute', onTrackMute)
    track.addEventListener('unmute', onTrackUnmute)
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  audioContext = new AudioContextCtor()
  // Safari/Chrome may return a suspended context outside a gesture; it reads
  // all-128 samples (RMS 0) so VAD would never arm.
  if (audioContext.state === 'suspended' && audioContext.resume) {
    await audioContext.resume().catch(function () {})
    if (myGeneration !== generation) {
      audioContext.close().catch(function () {})
      audioContext = null
      stream.getTracks().forEach(function (track) { track.stop() })
      stream = null
      return
    }
  }

  const source = audioContext.createMediaStreamSource(stream)
  analyser = audioContext.createAnalyser()
  analyser.fftSize = FFT_SIZE
  analyser.smoothingTimeConstant = 0.6

  // MediaRecorder stays on the raw stream (synthetic streams are unreliable in Safari).
  let detectionStream = stream
  if (isIOS()) {
    const gainNode = audioContext.createGain()
    gainNode.gain.value = IOS_GAIN_BOOST
    source.connect(gainNode)
    gainNode.connect(analyser)
    const destination = audioContext.createMediaStreamDestination()
    gainNode.connect(destination)
    detectionStream = destination.stream
  } else {
    source.connect(analyser)
  }

  openedAt = Date.now()
  lastTickAt = openedAt
  speechArmedFor = 0
  armGapMs = 0
  trueGapMs = 0
  speechDetectedAt = 0
  sawSpeechEnergy = false
  peakRms = 0
  lastSpeechThreshold = 0
  quietSince = 0
  noiseFloor = 0
  silenceFired = false
  calibrationLogged = false
  // A start after a no-op stop() never reaches releaseResources.
  sileroActive = false

  if (opts.record !== false) startRecorder()

  startTicking()

  // Not awaited (a few MB); a mid-utterance handover is safe via the shared funnels.
  vadSilero
    .attach({
      audioContext: audioContext,
      stream: detectionStream,
      redemptionMs: vadSilero.SILERO_REDEMPTION_MS,
      onSpeechStart: function () { markSpeechDetected('silero') },
      onSpeechEnd: function () { fireSilence('silero') }
    })
    .then(function (ok) {
      if (!ok) return
      if (myGeneration === generation) {
        sileroActive = true
        return
      }
      // Mic closed during load(); vadSilero's own guard misses this phase.
      vadSilero.detach()
    })
    .catch(function () { /* RMS stays in charge */ })
}

function startRecorder () {
  recordedChunks = []
  recorderMimeType = ''
  if (typeof window === 'undefined' || !window.MediaRecorder) return

  const mimeType = pickMimeType()
  try {
    recorder = mimeType
      ? new window.MediaRecorder(stream, { mimeType: mimeType })
      : new window.MediaRecorder(stream)
  } catch (e) {
    recorder = null
    return
  }

  recorderMimeType = recorder.mimeType || mimeType
  recorder.ondataavailable = function (event) {
    if (event.data && event.data.size > 0) recordedChunks.push(event.data)
  }
  try {
    recorder.start()
  } catch (e) {
    recorder = null
  }
}

// Durations use Date.now() so rAF throttling can't stretch the silence window.
function startTicking () {
  const freqData = new Uint8Array(analyser.frequencyBinCount)
  const timeData = new Uint8Array(analyser.fftSize)
  const levels = new Array(BAR_COUNT)
  const usableBins = Math.min(METER_BINS, analyser.frequencyBinCount)
  const binsPerBar = Math.max(1, Math.floor(usableBins / BAR_COUNT))

  const tick = function () {
    if (!analyser) return

    analyser.getByteFrequencyData(freqData)
    for (let i = 0; i < BAR_COUNT; i++) {
      let sum = 0
      for (let j = 0; j < binsPerBar; j++) {
        sum += freqData[i * binsPerBar + j] || 0
      }
      levels[i] = (sum / binsPerBar) / 255
    }
    emitLevels(levels)

    analyser.getByteTimeDomainData(timeData)
    updateVoiceActivity(rmsOf(timeData))

    rafId = requestAnimationFrame(tick)
  }

  tick()
}

function rmsOf (timeData) {
  let sumSquares = 0
  for (let i = 0; i < timeData.length; i++) {
    const sample = (timeData[i] - 128) / 128
    sumSquares += sample * sample
  }
  return Math.sqrt(sumSquares / timeData.length)
}

function emitLevels (levels) {
  for (let i = 0; i < levelSubscribers.length; i++) {
    try {
      levelSubscribers[i](levels)
    } catch (e) { /* a broken meter must never stop VAD */ }
  }
}

// Both detectors funnel through these; `silenceFired` ends an attempt once.
function markSpeechDetected (source) {
  // The Silero path skips the RMS bookkeeping that sets this.
  sawSpeechEnergy = true
  if (speechDetectedAt !== 0) return
  speechDetectedAt = Date.now()
  console.log('[micStream] speech detected (' + source + ')')
}

function fireSilence (source) {
  if (silenceFired) return
  // No utterance yet: the empty-attempt hint and hard cap own this case.
  if (speechDetectedAt === 0) return
  if (trackMuted) return
  silenceFired = true
  console.log('[micStream] silence fired (' + source + ')')
  const callback = silenceCallback
  if (callback) callback()
}

function updateVoiceActivity (rms) {
  const now = Date.now()
  const elapsedMs = Math.max(0, now - lastTickAt)
  lastTickAt = now

  if (sileroActive) return

  if (trackMuted) return

  // Calibrate for 250 ms, then keep tracking the room while quiet.
  const calibrating = now - openedAt < NOISE_CALIBRATION_MS
  if (calibrating) {
    noiseFloor = noiseFloor === 0 ? rms : noiseFloor * 0.8 + rms * 0.2
    return
  }

  if (!calibrationLogged) {
    calibrationLogged = true
    const st = Math.max(VAD_SPEECH_RMS, noiseFloor * VAD_NOISE_SPEECH_FACTOR)
    console.log('[micStream] calibrated noiseFloor=' + noiseFloor.toFixed(4) + ' speechThreshold=' + st.toFixed(4) + ' currentRms=' + rms.toFixed(4))
  }

  const speechThreshold = Math.max(VAD_SPEECH_RMS, noiseFloor * VAD_NOISE_SPEECH_FACTOR)
  const silenceThreshold = Math.max(VAD_SILENCE_RMS, noiseFloor * VAD_NOISE_SILENCE_FACTOR)

  if (rms > peakRms) peakRms = rms
  lastSpeechThreshold = speechThreshold

  // Energy uses the silence bar: on iPhone short sentences peak at 0.0123-0.0142
  // vs a speech bar of 0.0135-0.0150, while room noise sits at 0.0047-0.0062.
  if (rms >= silenceThreshold) sawSpeechEnergy = true

  if (rms >= speechThreshold) {
    // ponytail: diagnostic only, logs the true gap length on recovery.
    if (trueGapMs > 0 && speechDetectedAt === 0) {
      console.log('[micStream] true gap before recovery: ' + trueGapMs + 'ms (grace=' + VAD_ARM_GRACE_MS + 'ms)')
    }
    trueGapMs = 0
    speechArmedFor += elapsedMs
    armGapMs = 0
    quietSince = 0
    if (speechArmedFor >= VAD_ARM_MS && speechDetectedAt === 0) {
      markSpeechDetected('rms rms=' + rms.toFixed(4) + ' threshold=' + speechThreshold.toFixed(4))
    }
    return
  }

  if (speechDetectedAt === 0) trueGapMs += elapsedMs

  // While arming, a dip shorter than the grace is a syllable boundary.
  if (speechDetectedAt === 0 && speechArmedFor > 0) {
    armGapMs += elapsedMs
    if (armGapMs < VAD_ARM_GRACE_MS) return
    // ponytail: diagnostic only, remove once bug 1's persistence past the grace fix is explained.
    console.log('[micStream] rms arm reset after grace expired: had ' + speechArmedFor + 'ms banked, gap ran ' + armGapMs + 'ms (grace=' + VAD_ARM_GRACE_MS + 'ms), rms=' + rms.toFixed(4))
  }

  if (rms < silenceThreshold) {
    noiseFloor = noiseFloor * 0.98 + rms * 0.02
    if (speechDetectedAt === 0) {
      speechArmedFor = 0
      armGapMs = 0
      return
    }
    if (quietSince === 0) quietSince = now
    if (now - quietSince >= silenceMs) fireSilence('rms')
    return
  }

  if (speechDetectedAt === 0) {
    speechArmedFor = 0
    armGapMs = 0
  }
}

// Test seam: one RMS tick without a real analyser.
export function __tickForTests (rms, elapsedMs) {
  // Absolute, so fake-clock jumps between ticks don't leak into elapsedMs.
  if (typeof elapsedMs === 'number') lastTickAt = Date.now() - elapsedMs
  updateVoiceActivity(rms)
}

// Idempotent; concurrent calls share one promise. Resolves {blob, mimeType, durationMs} or null.
export function stop () {
  if (stopPromise) return stopPromise

  // Always bump: a start() waiting on permission has no stream to find.
  generation++

  if (!stream && !recorder && !audioContext) {
    silenceCallback = null
    return Promise.resolve(null)
  }

  stopPromise = finishRecording()
    .then(function (clip) {
      releaseResources()
      return clip
    })
    .catch(function () {
      releaseResources()
      return null
    })
    .then(function (clip) {
      stopPromise = null
      return clip
    })

  return stopPromise
}

// Flush the recorder BEFORE stopping tracks or the clip tail is lost.
function finishRecording () {
  const activeRecorder = recorder
  const startedAt = openedAt
  recorder = null

  if (!activeRecorder || activeRecorder.state === 'inactive') {
    return Promise.resolve(buildClip(startedAt))
  }

  return new Promise(function (resolve) {
    // This unblocks playback, so a stuck recorder must not wedge the drill.
    const guard = setTimeout(function () {
      resolve(buildClip(startedAt))
    }, 1500)

    activeRecorder.onstop = function () {
      clearTimeout(guard)
      resolve(buildClip(startedAt))
    }
    try {
      activeRecorder.stop()
    } catch (e) {
      clearTimeout(guard)
      resolve(buildClip(startedAt))
    }
  })
}

function buildClip (startedAt) {
  if (recordedChunks.length === 0) return null
  const type = recorderMimeType || recordedChunks[0].type || 'audio/webm'
  const blob = new Blob(recordedChunks, { type: type })
  recordedChunks = []
  if (blob.size === 0) return null
  return {
    blob: blob,
    mimeType: type,
    durationMs: startedAt ? Date.now() - startedAt : 0
  }
}

function releaseResources () {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  analyser = null
  silenceCallback = null

  // Before the AudioContext closes (Silero holds a worklet node on it).
  sileroActive = false
  vadSilero.detach()

  if (capturedTrack) {
    if (typeof capturedTrack.removeEventListener === 'function') {
      capturedTrack.removeEventListener('mute', onTrackMute)
      capturedTrack.removeEventListener('unmute', onTrackUnmute)
    }
    capturedTrack = null
  }
  trackMuted = false

  if (stream) {
    stream.getTracks().forEach(function (track) { track.stop() })
    stream = null
  }
  if (audioContext) {
    const closing = audioContext
    audioContext = null
    if (closing.close) closing.close().catch(function () {})
  }

  recordedChunks = []
  recorderMimeType = ''
  openedAt = 0
  speechArmedFor = 0
  armGapMs = 0
  trueGapMs = 0
  speechDetectedAt = 0
  quietSince = 0
  silenceFired = false
  calibrationLogged = false

  routeReadyPromise = nudgePlaybackRoute()
}

// iOS can keep PlayAndRecord after tracks stop: signal playback intent.
function nudgePlaybackRoute () {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return Promise.resolve()

  // Before the device check: must also reach iPads with a desktop UA.
  const hasAudioSession = typeof navigator.audioSession !== 'undefined'
  setAudioSessionType('playback')

  if (!isIOS()) return Promise.resolve()

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  if (!AudioContextCtor) return Promise.resolve()

  let ctx = null
  try {
    ctx = new AudioContextCtor()
  } catch (e) {
    return Promise.resolve()
  }

  // BUGFIX-005: diagnostic, did the buffer actually render or did the 500ms fallback resolve?
  console.log('[micStream] route nudge: audioSession API=' + (hasAudioSession ? 'yes' : 'no') + ' initial ctx.state=' + ctx.state)

  return new Promise(function (resolve) {
    let closed = false
    const close = function (reason) {
      if (closed) return
      closed = true
      console.log('[micStream] route nudge: ' + reason + ' (final ctx.state=' + (ctx ? ctx.state : 'closed') + ')')
      if (ctx && ctx.close) ctx.close().catch(function () {})
      ctx = null
      resolve()
    }

    const play = function () {
      try {
        const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * 0.05)), ctx.sampleRate)
        const source = ctx.createBufferSource()
        source.buffer = buffer
        source.connect(ctx.destination)
        source.onended = function () { close('rendered') }
        source.start(0)
      } catch (e) {
        close('threw: ' + (e && e.message))
      }
    }

    // Covers both resume() and render: WebKit may never settle resume()
    // outside a gesture, and whenRouteReady() gates the next sentence.
    setTimeout(function () { close('gave up waiting to render') }, 500)

    // Suspended outside a gesture; an unplayed context leaks and iOS caps them.
    if (ctx.state === 'suspended' && ctx.resume) {
      ctx.resume().then(play).catch(function () { close('resume() rejected') })
    } else {
      play()
    }
  })
}

export function subscribeLevels (fn) {
  if (typeof fn !== 'function') return
  if (levelSubscribers.indexOf(fn) === -1) levelSubscribers.push(fn)
}

export function unsubscribeLevels (fn) {
  const index = levelSubscribers.indexOf(fn)
  if (index !== -1) levelSubscribers.splice(index, 1)
}

export function __resetForTests () {
  // A pending rAF from a test that skipped stop() would corrupt the next test.
  if (rafId) cancelAnimationFrame(rafId)
  stream = null
  audioContext = null
  analyser = null
  recorder = null
  recordedChunks = []
  recorderMimeType = ''
  rafId = null
  levelSubscribers = []
  silenceCallback = null
  stopPromise = null
  openedAt = 0
  speechArmedFor = 0
  armGapMs = 0
  trueGapMs = 0
  speechDetectedAt = 0
  sawSpeechEnergy = false
  peakRms = 0
  lastSpeechThreshold = 0
  quietSince = 0
  noiseFloor = 0
  silenceFired = false
  calibrationLogged = false
  sileroActive = false
  generation = 0
  routeReadyPromise = Promise.resolve()
  trackMuted = false
  capturedTrack = null
  vadSilero.__resetForTests()
}
